import { registerLocaleData } from "@angular/common";
import { provideHttpClient, withInterceptors } from "@angular/common/http";
import localePt from "@angular/common/locales/pt";
import {
  APP_INITIALIZER,
  ErrorHandler,
  LOCALE_ID,
  importProvidersFrom,
  provideZoneChangeDetection,
} from "@angular/core";
import { MatDialogModule } from "@angular/material/dialog";
import { BrowserModule, bootstrapApplication } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { Router, provideRouter, withInMemoryScrolling } from "@angular/router";
import BugsnagPerformance from "@bugsnag/browser-performance";
import { BugsnagErrorHandler } from "@bugsnag/plugin-angular";
import * as Sentry from "@sentry/angular";
import { NgxMaskDirective, NgxMaskPipe, provideNgxMask } from "ngx-mask";

import { AppComponent } from "@app/app.component";
import { errorInterceptor } from "@core/interceptors/error/error.interceptor";
import { headersInterceptor } from "@core/interceptors/headers/headers.interceptor";

import { environment } from "./environments/environment";
import { routes } from "./routes";

import { BugsnagContextService } from "@shared/services/context/bugsnag-context.service";
import { version } from "../package.json";

if (environment.BUGSNAG_KEY) {
  BugsnagPerformance.start({ apiKey: environment.BUGSNAG_KEY, appVersion: version });
}

if (environment.SENTRY_DNS) {
  Sentry.init({
    dsn: environment.SENTRY_DNS,
    sendDefaultPii: true,
    release: version,
    environment: environment.environment,
    integrations: [Sentry.browserTracingIntegration()],
    enableLogs: true,
    tracesSampleRate: environment.SENTRY_TRACE_RATE,
    tracePropagationTargets: ["localhost", environment.urlApi],

    // drop all error/issues events
    beforeSend() {
      return null;
    },
    // keep transactions (performance)
    beforeSendTransaction(tx) {
      return tx;
    },
  });
}

registerLocaleData(localePt);

export function errorHandlerFactory() {
  if (environment.BUGSNAG_KEY) {
    return new BugsnagErrorHandler();
  }

  return new ErrorHandler();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    importProvidersFrom(BrowserModule, MatDialogModule, NgxMaskDirective, NgxMaskPipe),
    { provide: "Window", useValue: window },
    { provide: LOCALE_ID, useValue: "pt" },
    provideHttpClient(withInterceptors([headersInterceptor, errorInterceptor])),
    provideNgxMask(),
    { provide: ErrorHandler, useFactory: errorHandlerFactory },
    provideAnimations(),
    provideRouter(
      routes,
      withInMemoryScrolling({
        scrollPositionRestoration: "enabled",
      }),
    ),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    BugsnagContextService,
    {
      provide: APP_INITIALIZER,
      multi: true,
      deps: [BugsnagContextService],
      useFactory: (svc: BugsnagContextService) => () => svc.init(),
    },
  ],
})
  // eslint-disable-next-line no-console
  .catch((err) => console.error(err));
